import LiveHelpOutlinedIcon from '@mui/icons-material/LiveHelpOutlined';
import { ButtonBase, IconButton, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import { Funnel, FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import PaiButton from 'features/ui/base-components/pai-button';
import PaiDotLoader from 'features/ui/base-components/pai-dot-loader';
import PaiLink from 'features/ui/base-components/pai-link';
import PaiTooltip from 'features/ui/base-components/pai-tooltip';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReactFlow } from 'reactflow';
import { makeStyles } from 'tss-react/mui';
import ScheduleStartDialog from '../campaign-overview/schedule-start-dialog';
import AutoSyncIndicator from './campaign-building-panel/auto-sync-indicator';
import { CampaignBuilderSteps, CampaignPipelineSteps, ContactSearchMethods, Offer } from './campaign-builder.types';
import { coerceOfferFieldToString } from './offer-field.utils';

interface Props {
  productGroup: ProductGroups;
  campaign: Funnel | null;

  isUpdating: boolean;
  isFlowChanging: boolean;

  activeCampaignStepIndex: number;
  campaignStepsArray: CampaignBuilderSteps[];

  offer: Offer;
  activeCampaignPipelineStep: CampaignPipelineSteps;
  contactSearchMethod: ContactSearchMethods | '';
  linkedinAccountLoading: boolean;

  autoSyncLoading: boolean;
  autoSyncProcessingDialogOpen: boolean;

  startCampaignLoading: boolean;
  stopCampaignLoading: boolean;

  increaseCampaignStepIndex(): void;

  decreaseCampaignStepIndex(): void;

  selectCampaignStep(idx: number): () => void;

  handleOpenStopRunningCampaignDialog(campaignBuilderStep: CampaignBuilderSteps): () => void;

  handleOpenAutoSyncProcessingDialog(): void;

  handleOpenFaqDialog(): void;

  /** Gesetzt nur bei Recruiting-Entwürfen aus dem Assistenten: Link zurück in den Assistenten. */
  handleOpenWizard?: () => void;

  handleStartCampaign(): void;

  handleStopCampaign(): void;
}

/**
 * Single top bar of the campaign builder: campaign name, pill stepper and the step actions.
 */
const CampaignBuilderHeader: React.FC<Props> = ({
  productGroup,
  campaign,
  isUpdating,
  isFlowChanging,
  activeCampaignStepIndex,
  campaignStepsArray,
  offer,
  activeCampaignPipelineStep,
  contactSearchMethod,
  linkedinAccountLoading,
  autoSyncLoading,
  autoSyncProcessingDialogOpen,
  startCampaignLoading,
  stopCampaignLoading,
  increaseCampaignStepIndex,
  decreaseCampaignStepIndex,
  selectCampaignStep,
  handleOpenStopRunningCampaignDialog,
  handleOpenAutoSyncProcessingDialog,
  handleOpenFaqDialog,
  handleOpenWizard,
  handleStartCampaign,
  handleStopCampaign,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const { getNodes } = useReactFlow();

  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  const {
    _id = '',
    name: campaignName,
    status = FunnelStatuses.CREATED,
    linkedinAccount = '',
  } = campaign || {};

  const activeCampaignStepString = campaignStepsArray[activeCampaignStepIndex];

  const recruitingBoosterLogo = '/dashboard/images/logos/hr_booster_logo_short.svg';
  const leadBoosterLogo = '/dashboard/images/logos/lead_booster_logo_short.svg';

  const isCampaignRunning =
    status === FunnelStatuses.STARTED || status === FunnelStatuses.PAUSED || status === FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED;

  const getStatusText = () => {
    switch (status) {
      case FunnelStatuses.CREATED:
        return t('campaignStatusDraft');
      case FunnelStatuses.STARTED:
      case FunnelStatuses.PAUSED:
      case FunnelStatuses.CONTACTS_ALMOST_EXHAUSTED:
        return t('campaignStatusRunning');
      default:
        return t('campaignStatusEditing');
    }
  };

  const getSaveStateText = () => {
    if (isUpdating) return t('save');
    if (isFlowChanging) return t('waiting');
    return t('allChangesSaved');
  };

  const getNextButtonText = () => {
    switch (activeCampaignStepString) {
      case CampaignBuilderSteps.CAMPAIGN_BRIEFING:
        return t('toContactList');
      case CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST:
        return t('toCampaignFlow');
      case CampaignBuilderSteps.CAMPAIGN_FLOW:
        return t('toCampaignTesting');
      case CampaignBuilderSteps.CAMPAIGN_TESTING:
        return t('toCampaignSettings');
      case CampaignBuilderSteps.CAMPAIGN_SETTINGS:
        return t('toCampaignOverview');
      default:
        return t('next');
    }
  };

  const isNextDisabled = () => {
    const nodes = getNodes();

    // helper to safely check for empty strings/arrays/undefined
    const isEmptyValue = (value: any) => {
      if (typeof value === 'string') return value.trim() === '';
      if (Array.isArray(value)) return value.length === 0;
      return !value;
    };

    const jobTitle = coerceOfferFieldToString(offer?.jobTitle);
    const jobOffer = coerceOfferFieldToString(offer?.jobOffer);
    const problems = coerceOfferFieldToString(offer?.problems);
    const features = coerceOfferFieldToString(offer?.features);
    const benefits = coerceOfferFieldToString(offer?.benefits);
    const impact = coerceOfferFieldToString(offer?.impact);

    switch (activeCampaignStepString) {
      case CampaignBuilderSteps.CAMPAIGN_BRIEFING:
        if (productGroup === ProductGroups.HR_BOOSTER) {
          if (isEmptyValue(jobTitle) || isEmptyValue(jobOffer)) return true;
        }

        if (productGroup === ProductGroups.LEAD_BOOSTER) {
          if (isEmptyValue(problems) || isEmptyValue(features) || isEmptyValue(benefits) || isEmptyValue(impact)) return true;
        }

        return false;
      case CampaignBuilderSteps.CAMPAIGN_CONTACT_LIST:
        if (activeCampaignPipelineStep === CampaignPipelineSteps.ADD_CONTACTS && contactSearchMethod === '') {
          return true;
        }
        return false;
      case CampaignBuilderSteps.CAMPAIGN_FLOW:
        if (_.isEmpty(nodes)) return true;
        return false;
      case CampaignBuilderSteps.CAMPAIGN_SETTINGS:
        // Don't disable while LinkedIn account is still loading
        if (linkedinAccountLoading) {
          return false;
        }
        return !linkedinAccount;
      case CampaignBuilderSteps.CAMPAIGN_TESTING:
        return false;
      case CampaignBuilderSteps.CAMPAIGN_SUMMARY:
        return false;
      default:
        return false;
    }
  };

  return (
    <Stack className={classes.header} direction={'row'} alignItems={'center'} gap={2.5}>
      <img
        className={classes.logo}
        src={productGroup === ProductGroups.HR_BOOSTER ? recruitingBoosterLogo : productGroup === ProductGroups.LEAD_BOOSTER ? leadBoosterLogo : ''}
        alt={'product logo'}
      />
      <Stack className={classes.campaignInfo}>
        {!_.isNil(campaignName) ? (
          <PaiTooltip title={campaignName.length > 32 ? campaignName : undefined} placement={'bottom-start'}>
            <Typography className={classes.campaignName}>{campaignName}</Typography>
          </PaiTooltip>
        ) : null}
        <Stack direction={'row'} alignItems={'center'} gap={1}>
          {isUpdating || isFlowChanging ? <PaiDotLoader size={'small'} inline={true} /> : null}
          <Typography className={classes.statusText}>{`${getStatusText()} · ${getSaveStateText()}`}</Typography>
        </Stack>
      </Stack>

      <Stack className={classes.stepper} direction={'row'} alignItems={'center'} gap={0.75}>
        {campaignStepsArray.map((step, index) => {
          const active = index === activeCampaignStepIndex;
          const done = index < activeCampaignStepIndex;

          return (
            <ButtonBase
              key={step}
              className={cx(classes.step, active ? 'active' : done ? 'done' : undefined)}
              onClick={isCampaignRunning ? handleOpenStopRunningCampaignDialog(step) : selectCampaignStep(index)}
            >
              <span className={classes.stepNumber}>{index + 1}</span>
              {t(step)}
            </ButtonBase>
          );
        })}
      </Stack>

      <Stack className={classes.actions} direction={'row'} alignItems={'center'} gap={2}>
        {autoSyncLoading && !autoSyncProcessingDialogOpen ? (
          <AutoSyncIndicator dense={true} handleOpenAutoSyncProcessingDialog={handleOpenAutoSyncProcessingDialog} />
        ) : null}
        {handleOpenWizard && !isCampaignRunning ? (
          <PaiLink text={t('wizOpenWizard')} size={'sm'} underline={'always'} action={handleOpenWizard} disabled={isUpdating || isFlowChanging} />
        ) : null}
        <PaiTooltip title={t('frequentlyAskedQuestions')}>
          <IconButton className={classes.iconButton} size={'small'} onClick={handleOpenFaqDialog}>
            <LiveHelpOutlinedIcon className={classes.icon} />
          </IconButton>
        </PaiTooltip>
        {activeCampaignStepIndex > 0 && !isCampaignRunning ? (
          <PaiLink text={t('back')} size={'lg'} underline={'always'} action={decreaseCampaignStepIndex} disabled={isFlowChanging} />
        ) : null}
        {isCampaignRunning ? (
          <PaiButton
            text={t('stopCampaign')}
            border={'rounded'}
            action={handleStopCampaign}
            disabled={isUpdating || isFlowChanging}
            loading={stopCampaignLoading}
          />
        ) : activeCampaignStepIndex < campaignStepsArray.length - 1 ? (
          <PaiButton
            text={getNextButtonText()}
            border={'rounded'}
            action={increaseCampaignStepIndex}
            disabled={isNextDisabled() || isUpdating || isFlowChanging}
          />
        ) : (
          <>
            <PaiButton
              text={t('scheduleStart', '📅 Planen')}
              border={'rounded'}
              color={'secondary'}
              action={() => setScheduleDialogOpen(true)}
              disabled={isUpdating || isFlowChanging || status !== FunnelStatuses.CREATED}
            />
            <PaiButton
              text={t('startCampaign')}
              border={'rounded'}
              action={handleStartCampaign}
              disabled={isUpdating || isFlowChanging}
              loading={startCampaignLoading}
            />
            <ScheduleStartDialog
              open={scheduleDialogOpen}
              campaignId={_id}
              campaignName={campaignName || ''}
              linkedinAccountId={linkedinAccount || ''}
              onClose={() => setScheduleDialogOpen(false)}
              onScheduled={() => setScheduleDialogOpen(false)}
            />
          </>
        )}
      </Stack>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  header: {
    flexShrink: 0,
    background: '#FFF',
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  logo: {
    width: '28px',
    height: 'auto',
    flexShrink: 0,
  },
  campaignInfo: {
    gap: '2px',
    minWidth: 0,
    maxWidth: '260px',
  },
  campaignName: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: 500,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  statusText: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    whiteSpace: 'nowrap',
  },
  stepper: {
    marginLeft: '16px',
    minWidth: 0,
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  step: {
    flexShrink: 0,
    gap: '8px',
    height: '34px',
    padding: '0 12px',
    borderRadius: '17px',
    background: 'transparent',
    color: theme.palette.greys.medium,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 400,
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
    '& span': {
      background: theme.palette.greys.lighter,
      color: theme.palette.greys.medium,
    },
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
    },
    '&.done': {
      color: theme.palette.text.primary,
      '& span': {
        background: '#B0A9FF',
        color: '#FFF',
      },
    },
    '&.active': {
      background: theme.palette.backgrounds.main,
      color: theme.palette.primary.main,
      fontWeight: 500,
      '& span': {
        background: theme.palette.primary.main,
        color: '#FFF',
      },
    },
  },
  stepNumber: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '20px',
    height: '20px',
    borderRadius: '50%',
    fontSize: theme.typography.pxToRem(11),
  },
  actions: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  iconButton: {
    padding: '4px',
    '&:hover': {
      '& .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.greys.medium,
  },
}));

export default CampaignBuilderHeader;
